import { useState, useRef, useEffect, FormEvent } from 'react';

function useScrollReveal(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.opacity = '0';
    el.style.transform = 'translateY(28px)';
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.transition = 'opacity 0.9s ease, transform 0.9s ease';
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

// Número WhatsApp no formato internacional (sem + e sem espaços)
const WHATSAPP_NUMBER = '5511958814422';

export default function Contact() {
  const [sent, setSent] = useState(false);
  const leftRef = useScrollReveal();
  const rightRef = useScrollReveal();

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    // Impede comportamento padrão do formulário (sem reload, sem POST)
    e.preventDefault();

    const form = e.currentTarget;

    // Captura os valores dos campos
    const nome = (form.elements.namedItem('nome') as HTMLInputElement).value.trim();
    const email = (form.elements.namedItem('email') as HTMLInputElement).value.trim();
    const areaCaso = (form.elements.namedItem('area_caso') as HTMLSelectElement).value;
    const mensagem = (form.elements.namedItem('mensagem') as HTMLTextAreaElement).value.trim();

    // Monta a mensagem com estrutura exata solicitada
    const texto =
      `Nome Completo: ${nome} | ` +
      `E-mail: ${email} | ` +
      `Área do Caso: ${areaCaso} | ` +
      `Motivo do Contato: ${mensagem}`;

    // Codifica para URL e abre WhatsApp em nova aba
    const urlEncoded = encodeURIComponent(texto);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${urlEncoded}`, '_blank');

    // Exibe feedback de sucesso e reseta o formulário
    setSent(true);
    form.reset();
  };

  return (
    <section id="contato" className="py-28 bg-[#F5F3EE]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-px bg-[#C7A76C]"></div>
            <span className="font-sans text-[#C7A76C] text-xs font-medium tracking-widest uppercase">
              Entre em Contato
            </span>
            <div className="w-8 h-px bg-[#C7A76C]"></div>
          </div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#0F4A2B] font-semibold leading-tight">
            Fale agora com um especialista e{' '}
            <span className="text-[#C7A76C] italic">resolva sua situação</span>
          </h2>
          <p className="font-sans text-[#3A3A3A] text-base mt-5 leading-relaxed">
            Não deixe a burocracia ou a insegurança jurídica travar o futuro do seu patrimônio.
            Entre em contato agora — a primeira conversa é sem compromisso.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left — Contact Info */}
          <div ref={leftRef} className="flex flex-col gap-8">
            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/5511958814422?text=Olá%2C%20gostaria%20de%20falar%20com%20um%20especialista"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-5 bg-[#0F4A2B] hover:bg-[#0C3A22] text-white rounded-2xl px-7 py-6 transition-all duration-300 cursor-pointer group"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#C7A76C]/20 rounded-xl flex-shrink-0">
                <i className="ri-whatsapp-line text-[#C7A76C] text-3xl"></i>
              </div>
              <div>
                <p className="font-sans text-white/60 text-xs uppercase tracking-widest mb-1">WhatsApp</p>
                <p className="font-serif text-xl text-white font-semibold">(11) 95881-4422</p>
                <p className="font-sans text-white/50 text-sm mt-0.5">Clique para iniciar a conversa</p>
              </div>
              <div className="ml-auto w-8 h-8 flex items-center justify-center">
                <i className="ri-arrow-right-line text-[#C7A76C] text-xl group-hover:translate-x-1 transition-transform duration-200"></i>
              </div>
            </a>

            {/* Address */}
            <div className="flex items-start gap-5 bg-white border border-[#C7A76C]/15 rounded-2xl px-7 py-6">
              <div className="w-14 h-14 flex items-center justify-center bg-[#0F4A2B]/8 rounded-xl flex-shrink-0">
                <i className="ri-map-pin-2-line text-[#0F4A2B] text-2xl"></i>
              </div>
              <div>
                <p className="font-sans text-[#3A3A3A]/60 text-xs uppercase tracking-widest mb-1">Endereço</p>
                <p className="font-serif text-lg text-[#0F4A2B] font-semibold leading-snug">
                  Alameda Terracota, 185<br />sala 1213 — Cerâmica
                </p>
                <p className="font-sans text-[#3A3A3A] text-sm mt-1">São Caetano do Sul — SP</p>
              </div>
            </div>

            {/* Instagram */}
            <a
              href="https://www.instagram.com/brunocastro_adv/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-5 bg-white border border-[#C7A76C]/15 hover:border-[#C7A76C]/40 rounded-2xl px-7 py-6 transition-all duration-300 cursor-pointer group"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-[#0F4A2B]/8 rounded-xl flex-shrink-0">
                <i className="ri-instagram-line text-[#0F4A2B] text-2xl"></i>
              </div>
              <div>
                <p className="font-sans text-[#3A3A3A]/60 text-xs uppercase tracking-widest mb-1">Instagram</p>
                <p className="font-serif text-lg text-[#0F4A2B] font-semibold">@brunocastro_adv</p>
                <p className="font-sans text-[#3A3A3A]/60 text-sm mt-0.5">Siga para conteúdo jurídico</p>
              </div>
              <div className="ml-auto w-8 h-8 flex items-center justify-center">
                <i className="ri-external-link-line text-[#C7A76C] text-lg group-hover:scale-110 transition-transform duration-200"></i>
              </div>
            </a>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden border border-[#C7A76C]/15 h-52">
              <iframe
                title="Localização Bruno Castro Advocacia"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3654.5!2d-46.5498!3d-23.6234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ce5b!2sAlameda%20Terracota%2C%20185%2C%20S%C3%A3o%20Caetano%20do%20Sul!5e0!3m2!1spt-BR!2sbr!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Right — Form */}
          <div ref={rightRef} className="bg-white border border-[#C7A76C]/15 rounded-2xl p-8 md:p-10">
            <h3 className="font-serif text-2xl text-[#0F4A2B] font-semibold mb-2">
              Envie sua mensagem
            </h3>
            <p className="font-sans text-[#3A3A3A] text-sm mb-7">
              Preencha o formulário e você será direcionado ao WhatsApp com a mensagem pronta.
            </p>

            {sent ? (
              /* Feedback de sucesso */
              <div className="flex flex-col items-center gap-4 py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-[#0F4A2B]/10 rounded-full">
                  <i className="ri-whatsapp-line text-[#0F4A2B] text-3xl"></i>
                </div>
                <p className="font-serif text-xl text-[#0F4A2B] font-semibold">WhatsApp aberto!</p>
                <p className="font-sans text-[#3A3A3A] text-sm max-w-xs">
                  Sua mensagem foi montada e o WhatsApp foi aberto em uma nova aba. Basta clicar em enviar!
                </p>
                <button
                  onClick={() => setSent(false)}
                  className="text-[#C7A76C] font-sans text-sm font-medium hover:underline cursor-pointer mt-2 whitespace-nowrap"
                >
                  Enviar outra mensagem
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="contato-whatsapp"
                className="flex flex-col gap-4"
              >
                {/* Nome Completo */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="nome" className="font-sans text-xs font-medium text-[#3A3A3A] uppercase tracking-wide">
                    Nome Completo *
                  </label>
                  <input
                    id="nome"
                    type="text"
                    name="nome"
                    required
                    placeholder="Seu nome completo"
                    className="border border-[#E8E4DC] focus:border-[#C7A76C] bg-[#F5F3EE] rounded-md px-4 py-3 text-sm font-sans text-[#121212] outline-none transition-colors duration-200"
                  />
                </div>

                {/* E-mail */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="email" className="font-sans text-xs font-medium text-[#3A3A3A] uppercase tracking-wide">
                    E-mail *
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    required
                    placeholder="seu@email.com"
                    className="border border-[#E8E4DC] focus:border-[#C7A76C] bg-[#F5F3EE] rounded-md px-4 py-3 text-sm font-sans text-[#121212] outline-none transition-colors duration-200"
                  />
                </div>

                {/* Área do Caso */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="area_caso" className="font-sans text-xs font-medium text-[#3A3A3A] uppercase tracking-wide">
                    Área do Caso *
                  </label>
                  <select
                    id="area_caso"
                    name="area_caso"
                    required
                    defaultValue=""
                    className="border border-[#E8E4DC] focus:border-[#C7A76C] bg-[#F5F3EE] rounded-md px-4 py-3 text-sm font-sans text-[#121212] outline-none transition-colors duration-200 cursor-pointer"
                  >
                    <option value="" disabled>Selecione uma área</option>
                    <option value="Direito Trabalhista">Direito Trabalhista</option>
                    <option value="Direito Civil">Direito Civil</option>
                    <option value="Direito Previdenciário">Direito Previdenciário</option>
                    <option value="Direito de Família">Direito de Família</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>

                {/* Mensagem */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="mensagem" className="font-sans text-xs font-medium text-[#3A3A3A] uppercase tracking-wide">
                    Mensagem *
                  </label>
                  <textarea
                    id="mensagem"
                    name="mensagem"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="Descreva brevemente sua situação..."
                    className="border border-[#E8E4DC] focus:border-[#C7A76C] bg-[#F5F3EE] rounded-md px-4 py-3 text-sm font-sans text-[#121212] outline-none transition-colors duration-200 resize-none"
                  />
                  <p className="font-sans text-[#3A3A3A]/50 text-xs">Máximo 500 caracteres</p>
                </div>

                {/* Botão Enviar */}
                <button
                  type="submit"
                  className="flex items-center justify-center gap-2 bg-[#C7A76C] hover:bg-[#D6BA85] text-[#0C3A22] font-sans font-semibold text-sm px-8 py-4 rounded-md transition-all duration-300 cursor-pointer whitespace-nowrap mt-2"
                >
                  <i className="ri-whatsapp-line text-base"></i>
                  Enviar pelo WhatsApp
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
